import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles } from "lucide-react";
import HoverPreviewThumb from "@/HoverPreviewThumb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ──────────────────────────────────────────────────────────────────────────
 * DiscoverRail
 *
 * Rendered at the bottom of PublicViewer (`/s/:slug`). After a viewer
 * finishes a story, surface 3 thematically-paired stories from the rest
 * of the published library. Picked by gpt-4o-mini against title /
 * category / impact_summary, cached on the source-story doc for 7 days.
 *
 *  • Fetches `/api/stories/{slug}/discover`. If the rail is empty (less
 *    than 1 candidate published), the component returns null — no
 *    awkward "nothing here yet" placeholder.
 *  • Cards reuse HoverPreviewThumb so the gesture feels consistent with
 *    the homepage gallery.
 *  • Eyebrow copy intentionally human ("Stories you might also care
 *    about") — avoids algorithmic-feed vibes for a trauma-sensitive site.
 * ────────────────────────────────────────────────────────────────────── */
export default function DiscoverRail({ slug }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/stories/${slug}/discover?limit=3`);
        if (cancelled) return;
        setStories(Array.isArray(r.data?.stories) ? r.data.stories : []);
      } catch (_e) {
        if (!cancelled) setStories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading || stories.length === 0) return null;

  return (
    <section
      className="mt-12 md:mt-16 px-6 md:px-10"
      data-testid="discover-rail"
    >
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-baseline justify-between gap-4 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Discover
            </div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight">
              Stories you might also care about
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {stories.map((s) => (
            <a
              key={s.id}
              href={`/s/${s.slug}`}
              className="group panel overflow-hidden border border-white/5 hover:border-[color:var(--accent)]/40 transition-colors"
              data-testid={`discover-card-${s.slug}`}
            >
              <div className="relative aspect-video bg-black/40">
                <HoverPreviewThumb story={s} />
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] mb-1.5">
                  {(s.category || "general").replace(/_/g, " ")}
                </div>
                <div className="font-display text-lg leading-tight group-hover:text-[color:var(--accent)] transition-colors">
                  {s.title}
                </div>
                {s.impact_summary && (
                  <p className="text-xs text-[color:var(--muted)] mt-2 leading-relaxed line-clamp-2">
                    {s.impact_summary}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
